import type { SupportedLocale } from "./locales";

import { supported_locales_keys } from "./locales";

function applyTemplateWithOverrides(
  templateFn: (locale: SupportedLocale) => string,
  overrides?: Partial<Record<SupportedLocale, string>>
): Record<SupportedLocale, string> {
  return supported_locales_keys.reduce(
    (result, locale) => {
      result[locale] = overrides?.[locale] ?? templateFn(locale);
      return result;
    },
    {} as Record<SupportedLocale, string>
  );
}

/**
 * Creates a link template object for all supported locales.
 *
 * @param default_template - The default template to use for all locales.
 *                          Can be a string or a function that generates a template based on the locale.
 * @param options - Optional configuration object
 * @param options.overrides - Locale-specific template overrides
 *
 * @returns A record mapping each supported locale to its template string
 *
 * @example
 * // Simple string default
 * const template = createLinkTemplate("https://example.com/page");
 *
 * @example
 * // Function-based default with overrides
 * const template = createLinkTemplate(
 *   locale => `/${locale}/page`,
 *   {
 *     overrides: {
 *       es: "/es/pagina",
 *       fr: "/fr/page-fr"
 *     }
 *   }
 * );
 */
export function createLinkTemplate(
  default_template: string | ((locale: SupportedLocale) => string),
  options?: {
    overrides?: Partial<Record<SupportedLocale, string>>;
  }
): Record<SupportedLocale, string> {
  const templateFn =
    typeof default_template === "function"
      ? default_template
      : () => default_template;

  return applyTemplateWithOverrides(templateFn, options?.overrides);
}

export function prefixLinkTemplate(
  base_template: Record<SupportedLocale, string>,
  prefix: string
): Record<SupportedLocale, string> {
  return supported_locales_keys.reduce(
    (result, locale) => {
      result[locale] = prefix + base_template[locale];
      return result;
    },
    {} as Record<SupportedLocale, string>
  );
}

function resolveParamsForLocale<
  TParams extends Record<string, Record<SupportedLocale, string>>
>(params: TParams, locale: SupportedLocale): { [K in keyof TParams]: string } {
  return Object.keys(params).reduce(
    (localized_params, key) => {
      const param_value = params[key];
      if (!param_value || !(locale in param_value)) {
        // Ignore missing locale entries
        return localized_params;
      }

      localized_params[key as keyof TParams] = param_value[locale];
      return localized_params;
    },
    {} as { [K in keyof TParams]: string }
  );
}

/**
 * Creates a link template using parameterized terms that vary by locale.
 * This is useful when multiple localized terms need to be composed in a template.
 *
 * @param params - An object containing template parameters as Records mapping all supported locales to strings
 * @param templateFn - A function that receives the locale and localized param values
 * @param options - Optional configuration object
 * @param options.overrides - Locale-specific template overrides
 *
 * @returns A record mapping each supported locale to its complete template string
 *
 * @example
 * // Define template terms (must include all supported locales)
 * const template_term_bus = {
 *   en: "bus",
 *   es: "autobus",
 *   fr: "bus",
 *   // ... all other locales
 * };
 *
 * const route_link_template = createLinkTemplateWithParams(
 *   { bus: template_term_bus },
 *   (locale, { bus }) => `/${locale}/${bus}-{origin_name}-{destination_name}`
 * );
 *
 * // With overrides for specific locales
 * const custom_template = createLinkTemplateWithParams(
 *   { bus: template_term_bus },
 *   (locale, { bus }) => `/${locale}/${bus}-route`,
 *   {
 *     overrides: {
 *       en: "/en/special-bus-route"
 *     }
 *   }
 * );
 */
export function createLinkTemplateWithParams<
  TParams extends Record<string, Record<SupportedLocale, string>>
>(
  params: TParams,
  templateFn: (
    locale: SupportedLocale,
    localized_params: { [K in keyof TParams]: string }
  ) => string,
  options?: {
    overrides?: Partial<Record<SupportedLocale, string>>;
  }
): Record<SupportedLocale, string> {
  return applyTemplateWithOverrides(
    locale => templateFn(locale, resolveParamsForLocale(params, locale)),
    options?.overrides
  );
}
